import React, { useState, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/vi';
import './DateRangePickerModal.scss';
import viVN from 'antd/es/date-picker/locale/vi_VN';
import { CalendarPickerIcon } from '@assets/icon';

dayjs.locale('vi');

interface DateRangePickerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (dateRange: string) => void;
  reportType?: string;
}

const PRESET_RANGES = [
  { label: 'Hôm nay', getRange: () => [dayjs(), dayjs()] },
  { label: 'Hôm qua', getRange: () => [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
  { label: 'Tháng này', getRange: () => [dayjs().startOf('month'), dayjs()] },
  { label: 'Tháng trước', getRange: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: '30 ngày', getRange: () => [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'Năm nay', getRange: () => [dayjs().startOf('year'), dayjs()] },
];

const formatDateRange = (start: Dayjs, end: Dayjs, reportType?: string): string => {
  const startStr = start.format('DD/MM/YYYY');
  const endStr = end.format('DD/MM/YYYY');
  const dateRange = startStr === endStr
    ? startStr
    : `từ ngày ${startStr} - ${endStr}`;
  if (reportType) {
    return `${reportType} ${dateRange}`;
  }
  return dateRange;
};

const pickerLocale = {
  ...viVN,
  lang: {
    ...(viVN as any).lang,
    monthFormat: '[Tháng] MM  /',
    monthBeforeYear: true,
    yearFormat: 'YYYY',
    cellYearFormat: 'YYYY',

    shortMonths: [
      'Tháng 01',
      'Tháng 02',
      'Tháng 03',
      'Tháng 04',
      'Tháng 05',
      'Tháng 06',
      'Tháng 07',
      'Tháng 08',
      'Tháng 09',
      'Tháng 10',
      'Tháng 11',
      'Tháng 12',
    ],
  },
};

export const DateRangePickerModal: React.FC<DateRangePickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  reportType,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(true);
  const [customStart, setCustomStart] = useState<Dayjs | null>(null);
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
  const [startPanelMode, setStartPanelMode] = useState<any>('date');
  const [endPanelMode, setEndPanelMode] = useState<any>('date');
  const containerRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const canApply = selectedPreset !== null || (showCustom && customStart && customEnd);

  const handlePresetClick = (preset: typeof PRESET_RANGES[0]) => {
    setSelectedPreset(preset.label);
    setShowCustom(false);
  };

  const handleReset = () => {
    setSelectedPreset(null);
    setShowCustom(false);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleApply = () => {
    if (showCustom && customStart && customEnd) {
      const start = customStart.isBefore(customEnd) ? customStart : customEnd;
      const end = customStart.isBefore(customEnd) ? customEnd : customStart;
      onConfirm(formatDateRange(start, end, reportType));
    } else if (selectedPreset) {
      onConfirm(selectedPreset);
    }
    onClose();
  };

  return (
    <div className="drpm-fullscreen">
      <div className="drpm-header">
        <h3 className="drpm-title">Bộ lọc</h3>
        <button className="drpm-close" onClick={onClose}>✕</button>
      </div>

      <div className="drpm-content">
        <div className="drpm-section-label">
          <span>THỜI GIAN</span>
        </div>

        <div className="drpm-preset-grid">
          {PRESET_RANGES.map((preset) => (
            <button
              key={preset.label}
              className={`drpm-preset-btn${selectedPreset === preset.label ? ' active' : ''}`}
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <button
          className={`drpm-custom-btn${showCustom ? ' active' : ''}`}
          onClick={() => { setShowCustom(!showCustom); setSelectedPreset(null); }}
        >
          Tuỳ chỉnh thời gian
        </button>

        {showCustom && (
          <div className="drpm-date-row" ref={containerRef}>
            <div className="drpm-date-field">
              <label className="drpm-input-label">Từ ngày</label>
              <DatePicker
                presets={[]}
                locale={pickerLocale as any}
                value={customStart}
                mode={startPanelMode}
                popupClassName="drpm-picker-popup"
                onChange={(date) => {
                  setCustomStart(date);
                  if (customEnd && date && date.isAfter(customEnd)) {
                    setCustomEnd(null);
                  }
                }}
                onPanelChange={(_, mode) => {
                  if (mode !== 'date') setStartPanelMode('date');
                }}
                disabledDate={(current) =>
                  customEnd ? current.isAfter(customEnd, 'day') : false
                }
                suffixIcon={<CalendarPickerIcon style={{ width: '15.83px', height: '16.67px' }} />}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày"
                style={{ width: '100%' }}
                getPopupContainer={() => containerRef.current || document.body}
              />
            </div>
            <div className="drpm-date-field">
              <label className="drpm-input-label">Đến ngày</label>
              <DatePicker
                presets={[]}
                locale={pickerLocale as any}
                value={customEnd}
                mode={endPanelMode}
                popupClassName="drpm-picker-popup"
                onChange={(date) => {
                  setCustomEnd(date);
                  if (customStart && date && date.isBefore(customStart)) {
                    setCustomStart(null);
                  }
                }}
                onPanelChange={(_, mode) => {
                  if (mode !== 'date') setEndPanelMode('date');
                }}
                disabledDate={(current) =>
                  customStart ? current.isBefore(customStart, 'day') : false
                }
                suffixIcon={<CalendarPickerIcon style={{ width: '15.83px', height: '16.67px' }} />}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày"
                style={{ width: '100%' }}
                getPopupContainer={() => containerRef.current || document.body}
              />
            </div>
          </div>
        )}
      </div>

      <div className="drpm-actions">
        <button className="drpm-btn-reset" onClick={handleReset}>Thiết lập lại</button>
        <button
          className={`drpm-btn-confirm${!canApply ? ' disabled' : ''}`}
          onClick={handleApply}
          disabled={!canApply}
        >
          Áp dụng
        </button>
      </div>
    </div>
  );
};
